import { ReactNode, useCallback, useMemo } from 'react';
import { FCX, usePlatformFeatureFlag } from '@datassential/platform-ui-lib';
import HomeSectionHeader from '@pages/Home/components/HomeSectionHeader';
import HomeSectionTitle from '@pages/Home/components/HomeSectionTitle';
import HomeSectionDelimiter from '@pages/Home/components/HomeSectionDelimiter';
import HomeLnrCategory, { useHomeLnrCategory } from '@pages/Home/components/HomeLnrCategory';
import { useHomeWorkspace } from '@pages/Home/context/HomeWorkspaceContext';
import { useHomeCategoryLensAllSocial } from '@apiHooks/useHomeCategoryLensAllSocial';
import CategoryLensSocial from './CategoryLensSocial';
import CategoryLensMacs from './CategoryLensMacs';
import CategoryLensLtos from './CategoryLensLtos';
import CategoryLensChainLtos from './CategoryLensChainLtos/CategoryLensChainLtos';
import './HomeCategoryLens.scss';
import HomeCategoryReportsAiSummary from "../HomeCategoryReportsAiSummary/HomeCategoryReportsAiSummary";
import HomeCategoryRelatedCharts from "../HomeCategoryRelatedCharts/HomeCategoryRelatedCharts";
import cn from "classnames";
import { HomeWidgets } from '@pages/Home/constants';

interface HomeCategoryLensProps {
    extraContent?: ReactNode;
    nextSectionId?: string;
    visibleWidgets?: HomeWidgets[];
    compact?: boolean;
}

const HomeCategoryLens: FCX<HomeCategoryLensProps> = ({
    extraContent,
    nextSectionId,
    visibleWidgets,
    compact = false
}) => {
    const { chainId, chainName, visibleSections } = useHomeWorkspace();
    const isExtraFeaturesEnabled = usePlatformFeatureFlag('SNAP Category Lens Extra Features');
    const isSocialChartEnabled = usePlatformFeatureFlag('SNAP Category Lens Social');
    const {
        selectedCategoryId,
        setSelectedCategoryId,
        selectedCategoryName,
        options,
    } = useHomeLnrCategory();

    const showBottomDelimiter = useMemo(() => {
        if (!nextSectionId || nextSectionId === 'recent-searches') return false;
        return visibleSections.has(nextSectionId);
    }, [nextSectionId, visibleSections]);

    const allowedWidgets = useMemo(
        () => (visibleWidgets ? new Set(visibleWidgets) : null),
        [visibleWidgets],
    );
    const canRender = useCallback(
        (widget: HomeWidgets) => !allowedWidgets || allowedWidgets.has(widget),
        [allowedWidgets],
    );

    const { data: socialData, isLoading: isSocialLoading } = useHomeCategoryLensAllSocial();
    const socialKeywords = useMemo(
        () => socialData.items.get(selectedCategoryId) ?? [],
        [socialData, selectedCategoryId],
    );
    const hasSocialDataForCategory = useMemo(
        () => isSocialLoading || socialKeywords.length > 0,
        [isSocialLoading, socialKeywords.length],
    );

    const allCategoryIds = useMemo(
        () => options.map(({ id }) => id).filter(Boolean).join(','),
        [options],
    );

    const hasChain = chainId != null && !!chainName;

    const {
        showReportsAiSummary,
        showSocial,
        showMacs,
        showLtos,
        showChainLtos,
        showRelatedCharts,
        hasAnyVisibleBody,
    } = useMemo(() => {
        const showReportsAiSummary = isExtraFeaturesEnabled && canRender(HomeWidgets.ReportsAiSummary);
        const showSocial = isSocialChartEnabled && hasSocialDataForCategory && canRender(HomeWidgets.Social);
        const showMacs = canRender(HomeWidgets.Macs);
        const showLtos = canRender(HomeWidgets.Ltos);
        const showChainLtos = hasChain && canRender(HomeWidgets.ChainLtos);
        const showRelatedCharts = isExtraFeaturesEnabled && canRender(HomeWidgets.RelatedCharts);

        return {
            showReportsAiSummary,
            showSocial,
            showMacs,
            showLtos,
            showChainLtos,
            showRelatedCharts,
            hasAnyVisibleBody: showReportsAiSummary || showSocial || showMacs || showLtos || showChainLtos || showRelatedCharts || !!extraContent,
        };
    }, [
        isExtraFeaturesEnabled,
        isSocialChartEnabled,
        hasSocialDataForCategory,
        hasChain,
        canRender,
        extraContent,
    ]);

    if (!hasAnyVisibleBody) return null;

    return (
        <div className="Home__wrapper">
            <HomeSectionHeader>
                <div className="HomeCategoryLens__header">
                    <HomeSectionTitle testId="textCategoryLensTitle">Category Lens</HomeSectionTitle>
                    <HomeLnrCategory
                        className="HomeCategoryLens__select no-rem"
                        value={selectedCategoryId}
                        setValue={setSelectedCategoryId}
                        options={options}
                        testId="selectCategoryLensCategory"
                    />
                </div>
            </HomeSectionHeader>
            {showReportsAiSummary && (
                <div className="Home__sub-wrapper">
                    <HomeCategoryReportsAiSummary categoryName={selectedCategoryName}/>
                </div>
            )}
            {showSocial && (
                <div className={cn("Home__sub-wrapper min-height", isExtraFeaturesEnabled && "pad-top")}>
                    <CategoryLensSocial
                        categoryName={selectedCategoryName}
                        categoryId={selectedCategoryId}
                        keywords={socialKeywords}
                        isLoading={isSocialLoading}
                    />
                </div>
            )}
            {showMacs && (
                <div className={cn("Home__sub-wrapper", isExtraFeaturesEnabled && "pad-top")}>
                    <CategoryLensMacs categoryName={selectedCategoryName} categoryId={selectedCategoryId}/>
                </div>
            )}
            {showLtos && (
                <div className="Home__sub-wrapper">
                    <CategoryLensLtos
                        categoryName={selectedCategoryName}
                        categoryId={selectedCategoryId}
                    />
                </div>
            )}
            {showChainLtos && (
                <div className="Home__sub-wrapper">
                    <CategoryLensChainLtos
                        categoryName={selectedCategoryName}
                        categoryId={selectedCategoryId}
                        chainId={chainId as number}
                        chainName={chainName}
                        allCategoryIds={allCategoryIds}
                    />
                </div>
            )}
            {showRelatedCharts && (
                <div className={cn("Home__sub-wrapper", compact && "pad-top")}>
                    <HomeCategoryRelatedCharts categoryName={selectedCategoryName}/>
                </div>
            )}
            {extraContent}
            {showBottomDelimiter && (
                <HomeSectionDelimiter colors={['#1DFEEC', '#4FB0F9', '#00E1AF']}/>
            )}
        </div>
    );
};

export default HomeCategoryLens;
